<template>
    <div id="app" :class="{ 'is-mobileapp': GlobalState.enableMobileAppDesign.value }">
        <div v-if="rootStore.globalError" id="global-error" v-text="rootStore.globalError" />

        <Transition v-else name="fade" mode="out-in">
            <Layout v-if="shouldShowLogin">
                <Login />
            </Layout>
            <Loader v-else-if="shouldShowLoader" id="preloader" size="2xl"></Loader>
            <Layout :show-sidebar="!isPublicRoute" v-else>
                <router-view v-slot="{ Component }" :key="routerViewKey">
                    <Transition :name="routeTransition" mode="out-in">
                        <div v-if="!routeExists" id="error">The requested URL does not exist.</div>
                        <div v-else-if="!isAuthorized" id="error">You don't have access to the requested URL.</div>
                        <component :is="Component" v-else />
                    </Transition>
                </router-view>
            </Layout>
        </Transition>

        <OverlayContainer />

        <CContactHoverHost />

        <HelpPanel />

        <div id="popover-portal"></div>

        <Transition name="fade" mode="out-in">
            <div v-if="rootStore.globalLoader" id="root-loader">
                <div class="bg-wrap">
                    <Loader />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
import '@fontsource/inter/latin-200.css';
import '@fontsource/inter/latin-300.css';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-800.css';
import '@fontsource/inter/latin-900.css';
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-500.css';
import { OverlayContainer } from '@zyno-io/vue-foundation';
import { computed } from 'vue';

import CContactHoverHost from '@/modules/crm/exports/c-contact-hover-host.vue';
import HelpPanel from '@/shared/components/help-panel.vue';
import Layout from '@/shared/components/layout.vue';
import Loader from '@/shared/components/loader.vue';

import { useAppAuth } from './app.auth';
import { useAppMobileHelpers } from './app.mobile';
import { useAppRouter, useUrlTenantRouter } from './app.router';
import './app.sw';
import { useAppServiceWorker } from './app.sw';
import { useTenantConfiguration } from './app.tenant-configuration';
import { useZSWindowHelpers } from './app.zshelpers';
import { GlobalState } from './global';
import Login from './modules/core/login.vue';
import { MobileBridge } from './modules/mobile/helper/bridge';
import { useAutofillPrevention } from './shared/helpers/autofill.helpers';
import { usePageTitleHelpers } from './shared/helpers/page-title.helpers';
import { useIdleUpdateReload } from './shared/helpers/update-reload.helpers';
import './shared/styles/base.css';
import { useStore } from './store';

const rootStore = useStore();

const { hasLoadedPermissions, hasLoadedUserSession, isAuthorized } = useAppAuth();
const { isPublicRoute, isRouterReady, routeExists, routeTransition, routerViewKey } = useAppRouter();
const { isTenantRouted } = useUrlTenantRouter();
useAppServiceWorker();
useTenantConfiguration();

const shouldShowLogin = computed(() => {
    if (!isRouterReady.value) return false;
    if (!hasLoadedUserSession.value) return false;
    if (isPublicRoute.value) return false;
    if (GlobalState.user.value) return false;
    // In the native webview, session loss is handled by native ejection (see
    // MobileBridge.checkSession) — never surface the web login screen here.
    if (MobileBridge.isWithinZynoSuiteMobile) return false;
    return true;
});
const shouldShowLoader = computed(() => {
    if (!isRouterReady.value) return true;
    if (!hasLoadedUserSession.value) return true;
    if (!isTenantRouted.value) return true;
    if (isPublicRoute.value) return false;
    if (!hasLoadedPermissions.value) return true;
    return false;
});

usePageTitleHelpers();
useAutofillPrevention();
useAppMobileHelpers();
useZSWindowHelpers();

// Auto-reload the main app once a new version has shipped and the user has been
// idle for a couple of minutes. The SW checks /version.json (driven by the page
// heartbeat in sw.manager) and broadcasts it; UpdateChecker flips
// isUpdateAvailable. Without this, the authenticated shell only surfaces the
// manual "Update available" button in the sidebar. login.vue uses the same hook
// with a shorter idle; on workstation the 1.5-min session timeout hands off to it
// before this 2-min idle fires.
useIdleUpdateReload(2 * 60_000);
</script>

<style>
@reference '#tw.css';

#app {
    @apply flex-1 flex min-w-0;
}

#global-error {
    @apply flex-1 flex justify-center items-center bg-chrome text-2xl text-err-fill;
}

#popover-portal {
    @apply absolute w-full h-full top-0 left-0 pointer-events-none flex;
}

#preloader {
    @apply flex-1 justify-self-center self-center;
}

#root-loader {
    @apply absolute top-0 left-0 w-full h-full flex items-center justify-center p-4;

    .bg-wrap {
        @apply bg-surface-3 rounded-full shadow-lg;
    }
}

#error {
    @apply items-center justify-center text-xl text-err-fill;
}
</style>
